"use client";

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useParentBridge } from "../host";
import type {
  AppHelloPayload,
  AppNavigatePayload,
  AppReadyPayload,
  AppShowToastPayload,
  CustomEventPayload,
  WSContext,
  WSMessage,
} from "../types";
import { ParentType, WSEventType } from "../types";

interface IframeBridgeProps {
  url: string;
  appId: number;
  parentType?: ParentType | "workspace" | "organization"; // Type of parent window (default: "workspace")
  context?: WSContext;
  origin?: string; // Origin của iframe, mặc định là "*"
  className?: string;
  debug?: boolean;
  allow?: string;

  // Lifecycle callbacks
  onIframeLoad?: () => void;
  onWSHello?: (ctx: WSContext) => void;
  onOrgHello?: (ctx: WSContext) => void;
  onContextUpdate?: (ctx: WSContext) => void;

  // App -> parent events
  onAppHello?: (payload: AppHelloPayload) => void;
  onAppReady?: (payload: AppReadyPayload) => void;
  onAppContextRequest?: (payload: any) => void;

  // Validation
  onValidateUser?: (payload: AppHelloPayload) => boolean | Promise<boolean>; // Return false to reject

  // UI events
  onAppShowToast?: (payload: AppShowToastPayload) => void;

  // Navigation events
  onAppNavigate?: (payload: AppNavigatePayload) => void;
  onAppLogoutRequest?: () => void;
  onAppGoToAdmin?: () => void;
  onAppGoToOrg?: () => void;

  // Custom events
  onAppCustomEvent?: (payload: CustomEventPayload) => void;

  // Catch-all for any message
  onMessage?: (msg: WSMessage, event: MessageEvent) => void;

  // Loading
  showLoading?: boolean;
  loadingComponent?: React.ReactNode;

  // Error handling
  onError?: (error: Error) => void;
  onTimeout?: () => void;
}

export interface IframeBridgeRef {
  sendHello: (context: WSContext) => void;
  sendContextUpdate: (context: WSContext) => void;
  sendLanguageChanged: (locale: string) => void;
  sendWorkspaceChanged: (workspaceId: string, workspaceName?: string) => void;
  sendOrgChanged: (
    orgId: string,
    orgName?: string,
    workspaceId?: string,
    workspaceName?: string,
  ) => void;
  sendSessionExpired: () => void;
  navigateApp: (route: string, params?: Record<string, any>) => void;
  sendCustomEvent: (eventName: string, data?: any) => void;
  postToApp: (type: string, payload?: any) => void;
}

export const IframeBridge = forwardRef<IframeBridgeRef, IframeBridgeProps>(
  (
    {
      url,
      appId,
      parentType = ParentType.WORKSPACE,
      context,
      origin = "*",
      className = "w-full h-full border-0",
      onIframeLoad,
      onWSHello,
      onOrgHello,
      onContextUpdate,
      onAppHello,
      onAppReady,
      onAppContextRequest,
      onValidateUser,
      onAppShowToast,
      onAppNavigate,
      onAppLogoutRequest,
      onAppGoToAdmin,
      onAppGoToOrg,
      onAppCustomEvent,
      onMessage,
      showLoading = false,
      loadingComponent,
      onError,
      onTimeout,
      debug = false,
      allow = "",
    },
    ref,
  ) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [hasReceivedAppReady, setHasReceivedAppReady] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // DEBUG: Listen to ALL raw postMessage events when debug=true
    useEffect(() => {
      if (!debug) return;

      console.log(
        "[IframeBridge] 🔍 Debug mode enabled - listening to ALL postMessage events",
      );
      const debugHandler = (event: MessageEvent) => {
        console.log("[IframeBridge] 🔴 Raw postMessage received:", {
          origin: event.origin,
          data: event.data,
        });
      };
      window.addEventListener("message", debugHandler);
      return () => window.removeEventListener("message", debugHandler);
    }, [debug]);

    // Timeout detection
    useEffect(() => {
      const timeout = setTimeout(() => {
        if (!hasReceivedAppReady) {
          const error = new Error("App load timeout (30s)");
          if (debug) {
            console.error("[IframeBridge]", error);
          }
          onError?.(error);
          onTimeout?.();
        }
      }, 30000); // 30s timeout

      return () => clearTimeout(timeout);
    }, [hasReceivedAppReady, onError, onTimeout, debug]);

    // Sử dụng useParentBridge hook
    const { postToApp, sendHello, sendContextUpdate, navigateApp } =
      useParentBridge({
        parentType,
        debug,
        getIframeWindowByAppId: (id) => {
          if (id !== appId) return null;
          return iframeRef.current?.contentWindow ?? null;
        },
        getIframeOriginByAppId: (id) => {
          if (id !== appId) return null;
          return origin;
        },
        onMessage: async (msg, event) => {
          // Debug logging
          if (debug) {
            console.log("[IframeBridge] Received:", msg);
          }
          // Gọi callback tổng quát trước
          onMessage?.(msg, event);

          // Xử lý từng loại event cụ thể
          if (debug) {
            console.log(
              "[IframeBridge] handle message:",
              msg.from === "app" && msg.app_id === appId,
            );
          }
          if (msg.from === "app" && msg.app_id === appId) {
            switch (msg.type) {
              case WSEventType.APP_READY:
                if (debug) {
                  console.log("[IframeBridge] APP_READY", msg);
                }
                onAppReady?.(msg.payload as AppReadyPayload);
                setHasReceivedAppReady(true);
                setIsLoading(false);
                break;

              case WSEventType.APP_HELLO:
                if (debug) {
                  console.log("[IframeBridge] APP_HELLO", msg);
                }
                const helloPayload = msg.payload as AppHelloPayload;
                onAppHello?.(helloPayload);

                // Validate user if callback provided
                if (onValidateUser) {
                  const isValid = await Promise.resolve(
                    onValidateUser(helloPayload),
                  );
                  if (!isValid) {
                    if (debug) {
                      console.warn(
                        "[IframeBridge] User validation failed for APP_HELLO:",
                        helloPayload,
                      );
                    }
                    onError?.(new Error("User validation failed"));
                    break; // Don't send context if validation fails
                  }
                }

                // Auto gửi HELLO + context khi nhận APP_HELLO
                if (context) {
                  if (debug) {
                    console.log(
                      "[IframeBridge] Sending HELLO + context after APP_HELLO",
                    );
                  }
                  sendHello(appId, context);
                  if (parentType === ParentType.WORKSPACE) {
                    onWSHello?.(context);
                  }
                  if (parentType === ParentType.ORGANIZATION) {
                    onOrgHello?.(context);
                  }
                  sendContextUpdate(appId, context);
                  onContextUpdate?.(context);
                }
                break;

              case WSEventType.APP_CONTEXT_REQUEST:
                if (debug) {
                  console.log("[IframeBridge] APP_CONTEXT_REQUEST", msg);
                }
                onAppContextRequest?.(msg.payload);
                // Auto response với context nếu có
                if (context) {
                  sendContextUpdate(appId, context);
                }
                break;

              case WSEventType.APP_SHOW_TOAST:
                if (debug) {
                  console.log("[IframeBridge] APP_SHOW_TOAST", msg);
                }
                onAppShowToast?.(msg.payload as AppShowToastPayload);
                break;

              case WSEventType.APP_NAVIGATE:
                if (debug) {
                  console.log("[IframeBridge] APP_NAVIGATE", msg);
                }
                onAppNavigate?.(msg.payload as AppNavigatePayload);
                break;

              case WSEventType.APP_LOGOUT_REQUEST:
                if (debug) {
                  console.log("[IframeBridge] APP_LOGOUT_REQUEST", msg);
                }
                onAppLogoutRequest?.();
                break;

              case WSEventType.APP_GO_TO_ADMIN:
                if (debug) {
                  console.log("[IframeBridge] APP_GO_TO_ADMIN", msg);
                }
                onAppGoToAdmin?.();
                break;

              case WSEventType.APP_GO_TO_ORG:
                if (debug) {
                  console.log("[IframeBridge] APP_GO_TO_ORG", msg);
                }
                onAppGoToOrg?.();
                break;

              case WSEventType.APP_CUSTOM_EVENT:
                if (debug) {
                  console.log("[IframeBridge] APP_CUSTOM_EVENT", msg);
                }
                onAppCustomEvent?.(msg.payload as CustomEventPayload);
                break;

              default:
                // Log unknown events
                if (debug) {
                  console.warn("[IframeBridge] Unknown event type:", msg.type);
                }
            }
          }
        },
      });

    // Retry logic for sending messages
    const postToAppWithRetry = async (
      type: string,
      payload?: any,
      retries = 3,
    ) => {
      for (let i = 0; i < retries; i++) {
        try {
          postToApp(appId, type, payload);
          if (debug) {
            console.log(`[IframeBridge] Sent ${type}:`, payload);
          }
          return;
        } catch (error) {
          if (debug) {
            console.error(
              `[IframeBridge] Failed to send ${type} (attempt ${
                i + 1
              }/${retries}):`,
              error,
            );
          }
          if (i === retries - 1) {
            onError?.(error as Error);
            throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    };

    // Expose methods qua ref
    useImperativeHandle(ref, () => ({
      sendHello: (ctx: WSContext) => {
        sendHello(appId, ctx);
        if (parentType === ParentType.WORKSPACE) {
          onWSHello?.(ctx);
        }
        if (parentType === ParentType.ORGANIZATION) {
          onOrgHello?.(ctx);
        }
      },
      sendContextUpdate: (ctx: WSContext) => {
        sendContextUpdate(appId, ctx);
        onContextUpdate?.(ctx);
      },
      sendLanguageChanged: (locale: string) => {
        const eventType =
          parentType === ParentType.ORGANIZATION
            ? WSEventType.ORG_LANGUAGE_CHANGED
            : WSEventType.WS_LANGUAGE_CHANGED;
        postToApp(appId, eventType, { locale });
      },
      sendWorkspaceChanged: (workspaceId: string, workspaceName?: string) => {
        const eventType =
          parentType === ParentType.ORGANIZATION
            ? WSEventType.ORG_WORKSPACE_CHANGED
            : WSEventType.WS_WORKSPACE_CHANGED;
        postToApp(appId, eventType, {
          workspace_id: workspaceId,
          workspace_name: workspaceName,
        });
      },
      sendOrgChanged: (
        orgId: string,
        orgName?: string,
        workspaceId?: string,
        workspaceName?: string,
      ) => {
        const eventType =
          parentType === ParentType.ORGANIZATION
            ? WSEventType.ORG_WORKSPACE_CHANGED
            : WSEventType.WS_ORG_CHANGED;
        postToApp(appId, eventType, {
          org_id: orgId,
          org_name: orgName,
          workspace_id: workspaceId,
          workspace_name: workspaceName,
        });
      },
      sendSessionExpired: () => {
        const eventType =
          parentType === ParentType.ORGANIZATION
            ? WSEventType.ORG_SESSION_EXPIRED
            : WSEventType.WS_SESSION_EXPIRED;
        postToApp(appId, eventType, {});
      },
      navigateApp: (route: string, params?: Record<string, any>) => {
        navigateApp(appId, route, params);
      },
      sendCustomEvent: (eventName: string, data?: any) => {
        const eventType =
          parentType === ParentType.ORGANIZATION
            ? WSEventType.ORG_CUSTOM_EVENT
            : WSEventType.WS_CUSTOM_EVENT;
        postToApp(appId, eventType, {
          event_name: eventName,
          data,
        });
      },
      postToApp: (type: string, payload?: any) => {
        postToAppWithRetry(type, payload);
      },
    }));

    // Khi iframe load -> chỉ trigger callback, KHÔNG gửi context
    // Context sẽ được gửi khi nhận APP_HELLO từ iframe
    const handleIframeLoad = () => {
      onIframeLoad?.();

      if (debug) {
        console.log("[IframeBridge] Iframe loaded, waiting for APP_HELLO...");
      }
    };

    // Auto update context khi context prop thay đổi
    useEffect(() => {
      if (context && iframeRef.current?.contentWindow) {
        sendContextUpdate(appId, context);
        if (debug) {
          console.log("[IframeBridge] Context updated:", context);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(context), appId, sendContextUpdate, debug]);

    return (
      <>
        {showLoading && isLoading && loadingComponent}
        <iframe
          ref={iframeRef}
          src={url}
          onLoad={handleIframeLoad}
          className={className}
          title={`App ${appId}`}
          allow={allow}
        />
      </>
    );
  },
);

IframeBridge.displayName = "IframeBridge";
